/**
 * Performance Monitor
 * 
 * Tracks page load times, API response times, and cache performance
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      pageLoads: [],
      apiCalls: [],
      cacheHits: 0,
      cacheMisses: 0,
      totalRequests: 0,
    };
    
    this.thresholds = {
      pageLoad: 3000, // 3 seconds
      apiCall: 2000,  // 2 seconds
      cacheHitRate: 70, // 70%
    };
    
    this.init();
  }
  
  init() {
    // Monitor page load performance
    if (typeof window !== 'undefined' && window.performance) {
      window.addEventListener('load', () => {
        setTimeout(() => this.recordPageLoad(), 100);
      });
    }
  }
  
  recordPageLoad() {
    if (!window.performance || !window.performance.timing) return;
    
    const timing = window.performance.timing;
    const loadTime = timing.loadEventEnd - timing.navigationStart;
    const domReady = timing.domContentLoadedEventEnd - timing.navigationStart;
    
    const pageLoadMetric = {
      url: window.location.pathname,
      loadTime,
      domReady,
      timestamp: Date.now(),
      isSlowLoad: loadTime > this.thresholds.pageLoad,
    };
    
    this.metrics.pageLoads.push(pageLoadMetric);
    
    // Keep only last 50 page loads
    if (this.metrics.pageLoads.length > 50) {
      this.metrics.pageLoads = this.metrics.pageLoads.slice(-50);
    }
    
    // Log slow page loads
    if (pageLoadMetric.isSlowLoad) {
      console.warn(`[Performance] Slow page load: ${window.location.pathname} took ${loadTime}ms`);
    }
    
    console.debug(`[Performance] Page load: ${window.location.pathname} - ${loadTime}ms (DOM: ${domReady}ms)`);
  }
  
  recordApiCall(url, duration, fromCache = false) {
    this.metrics.totalRequests++;
    
    if (fromCache) {
      this.metrics.cacheHits++;
    } else {
      this.metrics.cacheMisses++;
      
      const apiCallMetric = {
        url,
        duration,
        timestamp: Date.now(),
        isSlowCall: duration > this.thresholds.apiCall,
      };
      
      this.metrics.apiCalls.push(apiCallMetric);
      
      // Keep only last 100 API calls
      if (this.metrics.apiCalls.length > 100) {
        this.metrics.apiCalls = this.metrics.apiCalls.slice(-100);
      }
      
      // Log slow API calls
      if (apiCallMetric.isSlowCall) {
        console.warn(`[Performance] Slow API call: ${url} took ${duration}ms`);
      }
    }
  }
  
  getCacheHitRate() {
    if (this.metrics.totalRequests === 0) return 0;
    return (this.metrics.cacheHits / this.metrics.totalRequests) * 100;
  }
  
  getAveragePageLoadTime() {
    if (this.metrics.pageLoads.length === 0) return 0;
    const total = this.metrics.pageLoads.reduce((sum, load) => sum + load.loadTime, 0);
    return total / this.metrics.pageLoads.length;
  }
  
  getAverageApiResponseTime() {
    if (this.metrics.apiCalls.length === 0) return 0;
    const total = this.metrics.apiCalls.reduce((sum, call) => sum + call.duration, 0);
    return total / this.metrics.apiCalls.length;
  }
  
  getSlowPageLoads() {
    return this.metrics.pageLoads.filter(load => load.isSlowLoad);
  }
  
  getSlowApiCalls() {
    return this.metrics.apiCalls.filter(call => call.isSlowCall);
  }
  
  getPerformanceReport() {
    const cacheHitRate = this.getCacheHitRate();
    const avgPageLoad = this.getAveragePageLoadTime();
    const avgApiResponse = this.getAverageApiResponseTime();
    
    return {
      summary: {
        averagePageLoadTime: Math.round(avgPageLoad),
        averageApiResponseTime: Math.round(avgApiResponse),
        cacheHitRate: Math.round(cacheHitRate * 10) / 10,
        totalRequests: this.metrics.totalRequests,
        cacheHits: this.metrics.cacheHits,
        cacheMisses: this.metrics.cacheMisses,
      },
      issues: {
        slowPageLoads: this.getSlowPageLoads().length,
        slowApiCalls: this.getSlowApiCalls().length,
        lowCacheHitRate: cacheHitRate < this.thresholds.cacheHitRate,
      },
      recommendations: this.getRecommendations(),
      recentMetrics: {
        pageLoads: this.metrics.pageLoads.slice(-10),
        apiCalls: this.metrics.apiCalls.slice(-10),
      }
    };
  }
  
  getRecommendations() {
    const recommendations = [];
    const cacheHitRate = this.getCacheHitRate();
    const avgPageLoad = this.getAveragePageLoadTime();
    const avgApiResponse = this.getAverageApiResponseTime();
    
    if (avgPageLoad > this.thresholds.pageLoad) {
      recommendations.push({
        type: 'page_load',
        message: `Average page load time (${Math.round(avgPageLoad)}ms) exceeds threshold (${this.thresholds.pageLoad}ms)`,
        suggestions: [
          'Enable more aggressive caching',
          'Optimize image loading',
          'Reduce bundle size',
          'Use lazy loading for non-critical components'
        ]
      });
    }
    
    if (avgApiResponse > this.thresholds.apiCall) {
      recommendations.push({
        type: 'api_performance',
        message: `Average API response time (${Math.round(avgApiResponse)}ms) exceeds threshold (${this.thresholds.apiCall}ms)`,
        suggestions: [
          'Implement API response caching',
          'Use optimized endpoints with minimal data',
          'Add request debouncing',
          'Consider pagination for large datasets'
        ]
      });
    }
    
    if (cacheHitRate < this.thresholds.cacheHitRate) {
      recommendations.push({
        type: 'cache_performance',
        message: `Cache hit rate (${Math.round(cacheHitRate)}%) is below threshold (${this.thresholds.cacheHitRate}%)`,
        suggestions: [
          'Increase cache TTL for stable data',
          'Enable caching for more endpoints',
          'Implement smarter cache invalidation',
          'Use browser caching headers'
        ]
      });
    }
    
    return recommendations;
  }
  
  // Debug method to log current performance state
  logPerformanceState() {
    const report = this.getPerformanceReport();
    console.group('[Performance Monitor] Current State');
    console.debug('Summary:', report.summary);
    console.debug('Issues:', report.issues);
    console.debug('Recommendations:', report.recommendations);
    console.groupEnd();
  }
  
  // Method to export performance data for analysis
  exportMetrics() {
    return {
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      metrics: this.metrics,
      report: this.getPerformanceReport(),
    };
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

// Export functions for use in other modules
export const recordApiCall = (url, duration, fromCache = false) => {
  performanceMonitor.recordApiCall(url, duration, fromCache);
};

export const getPerformanceReport = () => {
  return performanceMonitor.getPerformanceReport();
};

export const logPerformanceState = () => {
  performanceMonitor.logPerformanceState();
};

export const exportMetrics = () => {
  return performanceMonitor.exportMetrics();
};

// Add to window for debugging
if (typeof window !== 'undefined') {
  window.performanceMonitor = {
    getReport: getPerformanceReport,
    logState: logPerformanceState,
    exportMetrics: exportMetrics,
  };
}

export default performanceMonitor;